import React, { Component } from "react";
import CartContext from "../StoreManager/CartContext";
import {
  Table,
  Accordion,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
} from "react-bootstrap";

/**
 * FolioTransactions - shown inside Folio Detail modal.
 * Props:
 *  folioId, show, onClose, apiUrl(fn), fetchWithAuth(fn)
 */
class FolioTransactions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      transactions: [],
      loading: false,
      error: null,
      // transaction modal state
      showTxModal: false,
      editTx: null,
      txForm: this.emptyTx(),
      txSaving: false,
      summary: null,
    };
  }

  emptyTx() {
    return {
      folioId: this.props.folioId || "",
      description: "",
      accountCode: "",
      amount: 0,
      taxAmount: 0,
      currencyId: "",
      source: "",
      referenceNo: "",
    };
  }

  componentDidMount() {
    if (this.props.folioId) this.loadAll();
  }

  componentDidUpdate(prev) {
    if (this.props.folioId && prev.folioId !== this.props.folioId)
      this.loadAll();
    if (!this.props.show && prev.show) {
      // reset when modal closes
      this.setState({ transactions: [], summary: null, error: null });
    }
  }

  apiUrl = (resource) => {
    if (this.props.apiUrl) return this.props.apiUrl(resource);
    const base = (process.env.REACT_APP_API || "").replace(/\/+$/, "");
    const storeId = this.props.storeId || "";
    const sep = resource.includes("?") ? "&" : "?";
    return storeId
      ? `${base}/${resource}${sep}storeId=${encodeURIComponent(storeId)}`
      : `${base}/${resource}`;
  };

  fetchWithAuth = (url, opts = {}) => {
    if (this.props.fetchWithAuth) return this.props.fetchWithAuth(url, opts);
    const headers = { ...(opts.headers || {}) };
    if (opts.body && !headers["Content-Type"])
      headers["Content-Type"] = "application/json";
    const token = this.props.authToken;
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(url, { ...opts, headers });
  };

  loadAll = async () => {
    const { folioId } = this.props;
    if (!folioId) return;
    this.setState({ loading: true, error: null });
    try {
      const [txRes, summaryRes] = await Promise.all([
        this.fetchWithAuth(this.apiUrl(`HotelFolio/${folioId}/transactions`)),
        this.fetchWithAuth(this.apiUrl(`HotelFolio/${folioId}/summary`)),
      ]);

      if (!txRes.ok)
        throw new Error(await txRes.text().catch(() => txRes.statusText));
      if (!summaryRes.ok)
        throw new Error(
          await summaryRes.text().catch(() => summaryRes.statusText)
        );

      const [transactions, summary] = await Promise.all([
        txRes.json(),
        summaryRes.json(),
      ]);
      this.setState({ transactions, summary });
    } catch (err) {
      console.error("Error loading folio transactions:", err);
      this.setState({ error: err.message || "Failed to load transactions." });
    } finally {
      this.setState({ loading: false });
    }
  };

  openTxModal = (tx = null) => {
    this.setState({
      showTxModal: true,
      editTx: tx,
      txForm: tx ? { ...tx } : this.emptyTx(),
    });
  };

  closeTxModal = () =>
    this.setState({ showTxModal: false, editTx: null, txForm: this.emptyTx() });

  handleTxChange = (e) => {
    const { name, value } = e.target;
    this.setState((s) => ({ txForm: { ...s.txForm, [name]: value } }));
  };

  saveTx = async () => {
    const { txForm, editTx } = this.state;
    const { folioId } = this.props;
    if (!folioId) return;

    const api = this.apiUrl(`HotelFolio/${folioId}/transactions`);
    const url = editTx
      ? this.apiUrl(`HotelFolio/transactions/${editTx.folioTransactionId}`)
      : api;
    const method = editTx ? "PUT" : "POST";

    const payload = {
      description: txForm.description,
      accountCode: txForm.accountCode,
      amount: Number(txForm.amount) || 0,
      taxAmount: Number(txForm.taxAmount) || 0,
      currencyId: txForm.currencyId || null,
      source: txForm.source,
      referenceNo: txForm.referenceNo,
    };

    this.setState({ txSaving: true, error: null });
    try {
      const res = await this.fetchWithAuth(url, {
        method,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        throw new Error(text || `HTTP ${res.status}`);
      }
      await this.loadAll();
      this.closeTxModal();
    } catch (err) {
      console.error("Error saving transaction:", err);
      this.setState({ error: err.message || "Failed to save transaction." });
    } finally {
      this.setState({ txSaving: false });
    }
  };

  deleteTx = async (id) => {
    if (!window.confirm("Delete transaction?")) return;
    const url = this.apiUrl(`HotelFolio/transactions/${id}`);
    try {
      const res = await this.fetchWithAuth(url, { method: "DELETE" });
      if (!res.ok)
        throw new Error(await res.text().catch(() => res.statusText));
      await this.loadAll();
    } catch (err) {
      console.error("Error deleting transaction:", err);
      this.setState({ error: err.message || "Failed to delete transaction." });
    }
  };

  render() {
    const { show, onClose, folio, currencies = [] } = this.props;
    const {
      transactions,
      loading,
      error,
      showTxModal,
      txForm,
      txSaving,
      summary,
    } = this.state;

    return (
      <div>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" /> Loading...
          </div>
        ) : (
          <>
            <Row className="mb-2">
              <Col>
                <strong>Folio:</strong> {folio?.folioNo || "—"} <br />
                <small>Type: {folio?.folioType || "—"}</small>
              </Col>
              <Col className="text-end">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => this.openTxModal()}
                >
                  Add Transaction
                </Button>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col>
                <strong>Summary:</strong>{" "}
                {summary ? (
                  <span>
                    Charges:{" "}
                    {Number(
                      summary.totalCharges || summary.TotalCharges || 0
                    ).toFixed(2)}{" "}
                    | Taxes:{" "}
                    {Number(
                      summary.totalTaxes || summary.TotalTaxes || 0
                    ).toFixed(2)}{" "}
                    | Balance:{" "}
                    {Number(summary.balance || summary.Balance || 0).toFixed(2)}
                  </span>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </Col>
            </Row>

            <Table bordered hover responsive size="sm">
              <thead className="table-light">
                <tr>
                  <th>Description</th>
                  <th>Account</th>
                  <th>Amount</th>
                  <th>Tax</th>
                  <th>Currency</th>
                  <th>Source</th>
                  <th>Reference</th>
                  <th>Posted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 && (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No transactions
                    </td>
                  </tr>
                )}
                {transactions.map((t) => (
                  <tr key={t.folioTransactionId}>
                    <td>{t.description}</td>
                    <td>{t.accountCode}</td>
                    <td>{Number(t.amount || 0).toFixed(2)}</td>
                    <td>{Number(t.taxAmount || 0).toFixed(2)}</td>
                    <td>{t.currency?.isocode || "—"}</td>
                    <td>{t.source}</td>
                    <td>{t.referenceNo}</td>
                    <td>
                      {t.postedAt ? new Date(t.postedAt).toLocaleString() : "—"}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => this.openTxModal(t)}
                      >
                        Edit
                      </Button>{" "}
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => this.deleteTx(t.folioTransactionId)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}

        {/* Transaction Modal */}
        <Modal show={showTxModal} onHide={this.closeTxModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {txForm && txForm.folioTransactionId
                ? "Edit Transaction"
                : "Add Transaction"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-2">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  name="description"
                  value={txForm.description}
                  onChange={(e) => this.handleTxChange(e)}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Account Code</Form.Label>
                <Form.Control
                  name="accountCode"
                  value={txForm.accountCode}
                  onChange={(e) => this.handleTxChange(e)}
                />
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Amount</Form.Label>
                    <Form.Control
                      type="number"
                      name="amount"
                      value={txForm.amount}
                      onChange={(e) => this.handleTxChange(e)}
                      step="0.01"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Tax Amount</Form.Label>
                    <Form.Control
                      type="number"
                      name="taxAmount"
                      value={txForm.taxAmount}
                      onChange={(e) => this.handleTxChange(e)}
                      step="0.01"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-2">
                <Form.Label>Currency</Form.Label>
                <Form.Select
                  name="currencyId"
                  value={txForm.currencyId}
                  onChange={(e) => this.handleTxChange(e)}
                >
                  <option value="">-- Select --</option>
                  {(this.props.currencies || []).map((c) => (
                    <option key={c.currencyId} value={c.currencyId}>
                      {c.isocode}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Source</Form.Label>
                <Form.Control
                  name="source"
                  value={txForm.source}
                  onChange={(e) => this.handleTxChange(e)}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Reference No</Form.Label>
                <Form.Control
                  name="referenceNo"
                  value={txForm.referenceNo}
                  onChange={(e) => this.handleTxChange(e)}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={this.closeTxModal}
              disabled={txSaving}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={this.saveTx} disabled={txSaving}>
              {txSaving ? "Saving..." : "Save"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

/**
 * Inline component to fetch & display transactions for a single folio.
 * Uses the parent's apiUrl and fetchWithAuth to stay consistent with your logic.
 */
/* Replace existing FolioTransactionsInline with this enhanced version */
class FolioTransactionsInline extends Component {
  constructor(props) {
    super(props);
    this.state = {
      transactions: [],
      summary: null,
      loading: false,
      error: null,

      // add-transaction modal state
      showAddModal: false,
      txForm: this.emptyTxForm(),
      saving: false,
    };
  }

  emptyTxForm() {
    return {
      description: "",
      accountCode: "",
      amount: 0,
      taxAmount: 0,
      currencyId: "",
      source: "",
      referenceNo: "",
    };
  }

  componentDidMount() {
    if (this.props.folioId) this.load();
  }

  componentDidUpdate(prev) {
    if (prev.folioId !== this.props.folioId) this.load();
  }

  load = async () => {
    const { folioId, apiUrl, fetchWithAuth } = this.props;
    if (!folioId) return;
    this.setState({ loading: true, error: null });
    try {
      const txRes = await fetchWithAuth(
        apiUrl(`HotelFolio/${folioId}/transactions`)
      );
      const sumRes = await fetchWithAuth(
        apiUrl(`HotelFolio/${folioId}/summary`)
      );

      if (!txRes.ok) {
        const t = await txRes.text().catch(() => txRes.statusText);
        throw new Error(`Transactions request failed: ${txRes.status} ${t}`);
      }
      if (!sumRes.ok) {
        const s = await sumRes.text().catch(() => sumRes.statusText);
        throw new Error(`Summary request failed: ${sumRes.status} ${s}`);
      }

      const [transactions, summary] = await Promise.all([
        txRes.json(),
        sumRes.json(),
      ]);
      this.setState({ transactions, summary });
    } catch (err) {
      console.error("Error loading folio transactions:", err);
      this.setState({
        error: err.message || "Failed to load transactions.",
        transactions: [],
        summary: null,
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  // Add transaction handlers
  openAddModal = () =>
    this.setState({
      showAddModal: true,
      txForm: this.emptyTxForm(),
      error: null,
    });
  closeAddModal = () =>
    this.setState({
      showAddModal: false,
      txForm: this.emptyTxForm(),
      error: null,
    });

  handleTxChange = (e) => {
    const { name, value } = e.target;
    this.setState((s) => ({ txForm: { ...s.txForm, [name]: value } }));
  };

  saveTransaction = async () => {
    const { txForm } = this.state;
    const { folioId, apiUrl, fetchWithAuth } = this.props;

    // Basic validation
    if (!folioId) {
      this.setState({ error: "Folio not specified." });
      return;
    }
    if (!txForm.description) {
      this.setState({ error: "Description is required." });
      return;
    }
    if (!txForm.amount || isNaN(parseFloat(txForm.amount))) {
      this.setState({ error: "Amount is required." });
      return;
    }

    const payload = {
      description: txForm.description,
      accountCode: txForm.accountCode || null,
      amount: parseFloat(txForm.amount) || 0,
      taxAmount: parseFloat(txForm.taxAmount) || 0,
      currencyId: txForm.currencyId || null,
      source: txForm.source || null,
      referenceNo: txForm.referenceNo || null,
    };

    this.setState({ saving: true, error: null });
    try {
      const res = await fetchWithAuth(
        apiUrl(`HotelFolio/${folioId}/transactions`),
        {
          method: "POST",
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const txt = await res.text().catch(() => res.statusText);
        throw new Error(`Save failed: ${res.status} ${txt}`);
      }

      // refresh transactions & summary
      this.closeAddModal();
      await this.load();
    } catch (err) {
      console.error("Error saving transaction:", err);
      this.setState({ error: err.message || "Failed to save transaction." });
    } finally {
      this.setState({ saving: false });
    }
  };

  render() {
    const {
      transactions,
      summary,
      loading,
      error,
      showAddModal,
      txForm,
      saving,
    } = this.state;
    const { currencies = [] } = this.props;

    return (
      <div>
        {/* header area with summary + add button */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <strong>Summary:</strong>{" "}
            {summary ? (
              <>
                Charges:{" "}
                {(summary.totalCharges ?? summary.TotalCharges ?? 0).toFixed(2)}{" "}
                | Taxes:{" "}
                {(summary.totalTaxes ?? summary.TotalTaxes ?? 0).toFixed(2)} |
                Balance: {(summary.balance ?? summary.Balance ?? 0).toFixed(2)}
              </>
            ) : (
              "—"
            )}
          </div>
          <div>
            <Button size="sm" variant="success" onClick={this.openAddModal}>
              Add Transaction
            </Button>
          </div>
        </div>

        {loading && (
          <div className="mb-2">
            <Spinner animation="border" size="sm" /> Loading transactions...
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}

        <Table size="sm" bordered responsive>
          <thead className="table-light">
            <tr>
              <th>Description</th>
              <th>Account</th>
              <th>Amount</th>
              <th>Tax</th>
              <th>Currency</th>
              <th>Source</th>
              <th>Reference</th>
              <th>Posted</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 && (
              <tr>
                <td colSpan="8" className="text-center">
                  No transactions
                </td>
              </tr>
            )}
            {transactions.map((t) => (
              <tr key={t.folioTransactionId}>
                <td>{t.description}</td>
                <td>{t.accountCode}</td>
                <td>
                  {(t.amount || 0).toFixed
                    ? (t.amount || 0).toFixed(2)
                    : t.amount}
                </td>
                <td>
                  {(t.taxAmount || 0).toFixed
                    ? (t.taxAmount || 0).toFixed(2)
                    : t.taxAmount}
                </td>
                <td>{t.currency?.isocode || t.currency?.Isocode || "—"}</td>
                <td>{t.source}</td>
                <td>{t.referenceNo}</td>
                <td>
                  {t.postedAt ? new Date(t.postedAt).toLocaleString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Add Transaction Modal */}
        <Modal show={showAddModal} onHide={this.closeAddModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Add Transaction</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {this.state.error && (
              <Alert variant="danger">{this.state.error}</Alert>
            )}

            <Form>
              <Form.Group className="mb-2">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  name="description"
                  value={txForm.description}
                  onChange={this.handleTxChange}
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Account Code</Form.Label>
                <Form.Control
                  name="accountCode"
                  value={txForm.accountCode}
                  onChange={this.handleTxChange}
                  placeholder="E.g., ROOM_RENT"
                />
              </Form.Group>

              <Row>
                <Col md={6} className="mb-2">
                  <Form.Label>Amount</Form.Label>
                  <Form.Control
                    type="number"
                    name="amount"
                    value={txForm.amount}
                    onChange={this.handleTxChange}
                    step="0.01"
                  />
                </Col>
                <Col md={6} className="mb-2">
                  <Form.Label>Tax Amount</Form.Label>
                  <Form.Control
                    type="number"
                    name="taxAmount"
                    value={txForm.taxAmount}
                    onChange={this.handleTxChange}
                    step="0.01"
                  />
                </Col>
              </Row>

              <Form.Group className="mb-2">
                <Form.Label>Currency</Form.Label>
                <Form.Select
                  name="currencyId"
                  value={txForm.currencyId}
                  onChange={this.handleTxChange}
                >
                  <option value="">-- Select --</option>
                  {currencies.map((c) => (
                    <option key={c.currencyId} value={c.currencyId}>
                      {c.isocode || c.Isocode || c.isoCode}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Source</Form.Label>
                <Form.Control
                  name="source"
                  value={txForm.source}
                  onChange={this.handleTxChange}
                  placeholder="E.g., POS / RoomCharge"
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Reference No</Form.Label>
                <Form.Control
                  name="referenceNo"
                  value={txForm.referenceNo}
                  onChange={this.handleTxChange}
                />
              </Form.Group>
            </Form>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={this.closeAddModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={this.saveTransaction}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Transaction"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

/* =========================
   Main HotelFolio component
   (kept your logic exactly; UI replaced with Accordion)
   ========================= */
export default class HotelFolio extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      folios: [],
      reservations: [],
      currencies: [],
      loading: false,
      error: null,

      // folio modal
      showFolioModal: false,
      editFolio: null,
      folioForm: this.emptyFolio(),

      // folio detail modal (kept for compatibility)
      showFolioDetail: false,
      detailFolio: null,
    };
  }

  emptyFolio() {
    return {
      reservationId: "",
      folioNo: "",
      folioType: "Guest",
      currencyId: "",
    };
  }

  apiUrl = (resource) => {
    const baseRaw = (process.env.REACT_APP_API || "").replace(/\/+$/, "");
    const base = baseRaw || `${window.location.origin.replace(/\/+$/, "")}/api`;
    const path = resource.replace(/^\/+/, "");
    const storeId = this.context?.storeId;
    const sep = path.includes("?") ? "&" : "?";
    return storeId
      ? `${base}/${path}${sep}storeId=${encodeURIComponent(storeId)}`
      : `${base}/${path}`;
  };

  fetchWithAuth = async (url, opts = {}) => {
    const headers = { ...(opts.headers || {}) };
    if (
      opts.body &&
      !(opts.body instanceof FormData) &&
      !headers["Content-Type"]
    )
      headers["Content-Type"] = "application/json";
    const token = this.context?.authToken;
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(url, { ...opts, headers });
  };

  componentDidMount() {
    this.loadAllData();
  }

  loadAllData = async () => {
    const storeId = this.context?.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId not found in context." });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const [folioRes, resRes, curRes] = await Promise.all([
        this.fetchWithAuth(this.apiUrl("HotelFolio")),
        this.fetchWithAuth(this.apiUrl("HotelReservation")),
        this.fetchWithAuth(this.apiUrl("Currency")),
      ]);

      if (!folioRes.ok)
        throw new Error(await folioRes.text().catch(() => folioRes.statusText));
      if (!resRes.ok)
        throw new Error(await resRes.text().catch(() => resRes.statusText));
      if (!curRes.ok)
        throw new Error(await curRes.text().catch(() => curRes.statusText));

      const [folios, reservations, currencies] = await Promise.all([
        folioRes.json(),
        resRes.json(),
        curRes.json(),
      ]);
      this.setState({ folios, reservations, currencies });
    } catch (err) {
      console.error("Error loading folio data:", err);
      this.setState({ error: err.message || "Failed to load data." });
    } finally {
      this.setState({ loading: false });
    }
  };

  // Folio modal handlers (unchanged)
  openFolioModal = (folio = null) => {
    this.setState({
      showFolioModal: true,
      editFolio: folio,
      folioForm: folio
        ? {
            reservationId: folio.reservationId || "",
            folioNo: folio.folioNo || "",
            folioType: folio.folioType || "Guest",
            currencyId: folio.currencyId || "",
          }
        : this.emptyFolio(),
    });
  };

  closeFolioModal = () =>
    this.setState({ showFolioModal: false, editFolio: null });

  handleFolioChange = (e) => {
    const { name, value } = e.target;
    this.setState((s) => ({ folioForm: { ...s.folioForm, [name]: value } }));
  };

  saveFolio = async () => {
    const { folioForm, editFolio } = this.state;
    const storeId = this.context?.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId not found." });
      return;
    }

    const payload = {
      reservationId: folioForm.reservationId || null,
      folioNo: folioForm.folioNo,
      folioType: folioForm.folioType,
      currencyId: folioForm.currencyId || null,
    };

    const url = editFolio
      ? this.apiUrl(`HotelFolio/${editFolio.folioId}`)
      : this.apiUrl("HotelFolio");
    const method = editFolio ? "PUT" : "POST";

    try {
      const res = await this.fetchWithAuth(url, {
        method,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        throw new Error(text || `HTTP ${res.status}`);
      }
      this.closeFolioModal();
      await this.loadAllData();
    } catch (err) {
      console.error("Error saving folio:", err);
      this.setState({ error: err.message || "Failed to save folio." });
    }
  };

  deleteFolio = async (id) => {
    if (!window.confirm("Delete folio?")) return;
    try {
      const res = await this.fetchWithAuth(this.apiUrl(`HotelFolio/${id}`), {
        method: "DELETE",
      });
      if (!res.ok)
        throw new Error(await res.text().catch(() => res.statusText));
      await this.loadAllData();
    } catch (err) {
      console.error("Error deleting folio:", err);
      this.setState({ error: err.message || "Failed to delete folio." });
    }
  };

  // Folio detail (kept for compatibility; not used by accordion body)
  openFolioDetail = (folio) => {
    this.setState({ showFolioDetail: true, detailFolio: folio });
  };

  closeFolioDetail = () => {
    this.setState({ showFolioDetail: false, detailFolio: null });
  };

  render() {
    const {
      folios,
      reservations,
      currencies,
      loading,
      error,
      showFolioModal,
      folioForm,
      editFolio,
      showFolioDetail,
      detailFolio,
    } = this.state;
    const storeId = this.context?.storeId;
    const authToken = this.context?.authToken;

    return (
      <div className="container mt-4">
        <Row className="mb-3 align-items-center">
          <Col>
            <h4>Hotel Folios</h4>
          </Col>
          <Col className="text-end">
            <Button onClick={() => this.openFolioModal()} disabled={loading}>
              Add Folio
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div>Loading...</div>
        ) : (
          <>
            <Accordion
            // defaultActiveKey={folios.length > 0 ? folios[0].folioId : ""}
            // alwaysOpen
            >
              {folios.length === 0 && (
                <div className="text-center text-muted">No folios</div>
              )}

              {folios.map((f) => (
                <Accordion.Item eventKey={f.folioId} key={f.folioId}>
                  <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-center w-100">
                      <div>
                        <strong>{f.folioNo || "(No Number)"}</strong>
                        <div className="small text-muted">
                          Reservation: {f.reservation?.reservationNo || "—"} |{" "}
                          Type: {f.folioType} | Created:{" "}
                          {f.createdAt ? f.createdAt.split("T")[0] : "—"} |{" "}
                          Currency: {f.currency?.isocode || "—"}
                        </div>
                      </div>
                    </div>
                  </Accordion.Header>

                  <Accordion.Body>
                    <Row>
                      <Col md={6}>
                        <p>
                          <strong>Reservation:</strong>{" "}
                          {f.reservation?.reservationNo || "—"}
                        </p>
                        <p>
                          <strong>Type:</strong> {f.folioType}
                        </p>
                      </Col>
                      <Col md={6}>
                        <p>
                          <strong>Currency:</strong>{" "}
                          {f.currency?.isocode || "—"}
                        </p>
                        <p>
                          <strong>Created:</strong>{" "}
                          {f.createdAt ? f.createdAt.split("T")[0] : "—"}
                        </p>
                      </Col>
                    </Row>

                    <div className="mb-3">
                      <FolioTransactionsInline
                        folioId={f.folioId}
                        apiUrl={this.apiUrl}
                        fetchWithAuth={this.fetchWithAuth}
                        currencies={currencies}
                        storeId={storeId}
                        authToken={authToken}
                      />
                    </div>

                    <div className="d-flex justify-content-end">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="me-2"
                        onClick={() => this.openFolioModal(f)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-info"
                        className="me-2"
                        onClick={() => this.openFolioDetail(f)}
                      >
                        Transactions (Modal)
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => this.deleteFolio(f.folioId)}
                      >
                        Delete
                      </Button>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </>
        )}

        {/* Folio Modal */}
        <Modal
          show={showFolioModal}
          onHide={() => this.closeFolioModal()}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{editFolio ? "Edit Folio" : "Add Folio"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Reservation</Form.Label>
                    <Form.Select
                      name="reservationId"
                      value={folioForm.reservationId}
                      onChange={(e) => this.handleFolioChange(e)}
                    >
                      <option value="">-- Select --</option>
                      {reservations.map((r) => (
                        <option key={r.reservationId} value={r.reservationId}>
                          {r.reservationNo}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Currency</Form.Label>
                    <Form.Select
                      name="currencyId"
                      value={folioForm.currencyId}
                      onChange={(e) => this.handleFolioChange(e)}
                    >
                      <option value="">-- Select --</option>
                      {currencies.map((c) => (
                        <option key={c.currencyId} value={c.currencyId}>
                          {c.isocode}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-2">
                <Form.Label>Folio No</Form.Label>
                <Form.Control
                  name="folioNo"
                  value={folioForm.folioNo}
                  onChange={(e) => this.handleFolioChange(e)}
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Folio Type</Form.Label>
                <Form.Select
                  name="folioType"
                  value={folioForm.folioType}
                  onChange={(e) => this.handleFolioChange(e)}
                >
                  <option value="Guest">Guest</option>
                  <option value="Master">Master</option>
                  <option value="CityLedger">CityLedger</option>
                </Form.Select>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => this.closeFolioModal()}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => this.saveFolio()}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Folio Detail (Transactions + Summary) Modal - unchanged */}
        <Modal
          show={showFolioDetail}
          onHide={this.closeFolioDetail}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Folio Transactions</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {detailFolio ? (
              <FolioTransactionsInline
                folioId={detailFolio.folioId}
                apiUrl={this.apiUrl}
                fetchWithAuth={this.fetchWithAuth}
                currencies={currencies}
                storeId={storeId}
                authToken={authToken}
              />
            ) : (
              <div>Loading...</div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.closeFolioDetail}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
